import { CustomValidator } from "../../common/validation/types";
import { credentialState } from "../../common/validation/hasCredential";
import { isValidCuitCuil } from "../../common/validation/cuit";
import { TadManagerConfigValuesType } from "./TadManagerConfigCommonStore";

/**
 * Espejo UX de las validaciones del server para TadManagerConfig
 * (validateTadManagerCuitFields, validateTadManagerAuthProviderRequiresCuit y
 * validateTadManagerAuthProviderRequiresCredential). La autoridad es el server:
 * esto sólo evita que el SysAdmin descubra el problema recién al guardar.
 */

type CredentialFieldName =
  | "procedureNumber"
  | "miArgentinaPassword"
  | "arcaPassword"
  | "ansesPassword";

/**
 * Presencia de una credencial `.secret` como tri-estado sobre el primitivo
 * compartido `credentialState`: `true` hay, `false` no hay, `undefined`
 * todavía no se sabe.
 *
 * El aporte local es reconocer cuándo el `<campo>HasValue` de `values` no es
 * una respuesta del server: mientras el registro no llegó es el `false` del
 * init del store, y colapsar ese desconocimiento en "falta" le muestra el error
 * a quien todavía no vio el formulario.
 */
const fieldCredentialState = (
  values: TadManagerConfigValuesType,
  field: CredentialFieldName
): boolean | undefined =>
  credentialState(
    values.id ? (values[`${field}HasValue`] as boolean | undefined) : undefined,
    values[field]
  );

// Sólo `false` (se sabe que falta) es error; `undefined` no afirma nada.
const isMissing = (state: boolean | undefined) => state === false;

/**
 * Dígito verificador del CUIT/CUIL del gestor (módulo 11), sólo cuando el tipo
 * de documento efectivo es cuitCuil — con DNI no aplica — y sólo sobre el campo
 * con valor, para no bloquear la edición de datos legacy. Sin esto, el formulario
 * acepta la máscara y es el server el que rechaza al guardar.
 */
export const tadManagerConfigCuitCheckDigitValidator: CustomValidator<TadManagerConfigValuesType> =
  {
    name: "tadManagerConfigCuitCheckDigitValidator",
    validate(values, langStore) {
      const errors: Record<string, { message: string; fieldLabel: string }> =
        {};

      const idCardType = values.idCardType || "cuitCuil";
      if (
        idCardType === "cuitCuil" &&
        values.idCard?.trim() &&
        !isValidCuitCuil(values.idCard)
      ) {
        errors.idCard = {
          message: langStore.cuitCheckDigitInvalid,
          fieldLabel: langStore.TadManagerConfigIdCardLabel,
        };
      }

      return { errors, cardErrors: {} };
    },
  };

export const tadManagerConfigAuthProviderValidator: CustomValidator<TadManagerConfigValuesType> =
  {
    name: "tadManagerConfigAuthProviderValidator",
    validate(values, langStore) {
      const errors: Record<string, { message: string; fieldLabel: string }> =
        {};

      const provider = values.authProvider || "renaper";
      const idCardType = values.idCardType || "cuitCuil";

      // Regla A: sólo RENAPER acepta DNI; los otros tres proveedores se loguean
      // con el CUIT/CUIL de 11 dígitos.
      if (provider !== "renaper" && idCardType !== "cuitCuil") {
        errors.idCardType = {
          message: langStore.tadManagerAuthProviderRequiresCuit,
          fieldLabel: langStore.TadManagerConfigIdCardTypeLabel,
        };
      }

      // Regla B: el proveedor elegido exige su propia credencial. Sin registro
      // cargado no se afirma nada (ver fieldCredentialState). El género va aparte
      // del mapa proveedor→credencial: RENAPER lo exige para loguearse, pero no
      // es una credencial y su valor guardado sí se puede mirar.
      if (provider === "renaper") {
        if (isMissing(fieldCredentialState(values, "procedureNumber"))) {
          errors.procedureNumber = {
            message: langStore.tadManagerAuthProviderRequiresCredential,
            fieldLabel: langStore.TadManagerConfigProcedureNumberLabel,
          };
        }
        if (values.id && !values.gender?.trim()) {
          errors.gender = {
            message: langStore.tadManagerAuthProviderRequiresCredential,
            fieldLabel: langStore.TadManagerConfigGenderLabel,
          };
        }
      } else if (
        provider === "miArgentina" &&
        isMissing(fieldCredentialState(values, "miArgentinaPassword"))
      ) {
        errors.miArgentinaPassword = {
          message: langStore.tadManagerAuthProviderRequiresCredential,
          fieldLabel: langStore.TadManagerConfigMiArgentinaPasswordLabel,
        };
      } else if (
        provider === "arca" &&
        isMissing(fieldCredentialState(values, "arcaPassword"))
      ) {
        errors.arcaPassword = {
          message: langStore.tadManagerAuthProviderRequiresCredential,
          fieldLabel: langStore.TadManagerConfigArcaPasswordLabel,
        };
      } else if (
        provider === "anses" &&
        isMissing(fieldCredentialState(values, "ansesPassword"))
      ) {
        errors.ansesPassword = {
          message: langStore.tadManagerAuthProviderRequiresCredential,
          fieldLabel: langStore.TadManagerConfigAnsesPasswordLabel,
        };
      }

      return { errors, cardErrors: {} };
    },
  };
